import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type { NumberOfMembers } from '@/javascript/interfaces'

import { getDefaultCourse } from '../helpers/_constants'
import { getMemberDetails, postNotes } from '../services/administratorDashboard'
import type {
	BackOfficeIntegration,
	EligibleAgency,
	Note,
	NotesData,
} from '../services/administratorDashboard/interfaces'
import api from '../services/api'
import type { Goal, HistoricalTrainingGoal } from '../services/goals/interfaces'
import {
	getMemberSettings,
	putEditMemberSettings,
	putUpdateAnnualYear,
} from '../services/members'
import type {
	Member,
	MemberDetails,
	MemberSettings,
} from '../services/members/interfaces'
import type { Group } from '../services/team/interfaces'
import { getAgencyId } from '../utils/getAgencyId'
import GlobalStore from './GlobalStore'
import { useMemberTrainingStore } from './MemberTrainingStore'
import { useTeamStore } from './TeamStore'

export const useMembersStore = defineStore('members', () => {
	// State
	const additionalTraining = ref<HistoricalTrainingGoal>({} as HistoricalTrainingGoal)
	const agencyHasBirthdayReq = ref(false)
	const agencyStateRegistration = ref(false)
	const backOfficeIntegration = ref<BackOfficeIntegration>({
		enabled: false,
		providerName: '',
	})
	const backToAdministratorDashboardUrl = ref('')
	const backToAdministratorLinkType = ref('')
	const currentCourseStatusId = ref<number | null>(null)
	const eligibleAgencies = ref<EligibleAgency[]>([])
	const excludeNewTrainingBundleId = ref<number | null>(null)
	const filteredMemberIds = ref<number[]>([])
	const goal = ref<Goal | null>(null)
	const goals = ref<Goal[]>([])
	const groups = ref<Group[]>([])
	const isAdvancedAutomationEnabled = ref(false)
	const isHireDateTheBaseDateForComplianceAnnualDueDate = ref(false)
	const isLoadingMember = ref(false)
	const isSuper = ref(false)
	const manageGroups = ref(false)
	const memberCount = ref(0)
	const memberCreatedAt = ref('')
	const memberDetails = ref<MemberDetails | null>(null)
	const memberFilter = ref('')
	const memberList = ref<Member[]>([])
	const memberNotes = ref<Note[]>([])
	const memberSettings = ref<MemberSettings>({} as MemberSettings)
	const membersActiveCount = ref(0)
	const notStartedMembers = ref(0)
	const numberOfMembers = ref<NumberOfMembers>({
		dueSoon: 0,
		overdue: 0,
		total: 0,
		untrained: 0,
	})
	const shouldDisplayInProgressMessage = ref(false)
	const showDueDate = ref(false)
	const updatedAnnualDueDate = ref<{ date: string; id: number | null }>({
		date: '',
		id: null,
	})

	// Getters
	const filteredMembers = computed((): Member[] => {
		return filteredMemberIds.value?.length
			? memberList.value.filter(member => filteredMemberIds.value.includes(member.id))
			: memberList.value
	})

	const memberActionBody = computed(() => {
		if (GlobalStore.bulkAction.action.toLowerCase() === 'deactivate') {
			return <span>
				<b>This action is for permanent departures only.</b>
				<br /><br />
				Repeatedly deactivating and reactivating the same team member—referred to as seat rotation—can interrupt
				training records and compliance tracking.
				Excessive use of this practice may fall outside your service agreement and could trigger a billing review.
			</span>
		}

		return <>
			{GlobalStore.bulkAction.actionDescriptionText}
			{GlobalStore.bulkAction.additionalMembersText}
			<br /><br />
			<span class="text-brand-orange-400 font-medium">
				Are you sure you want to {GlobalStore.bulkAction.action} {GlobalStore.bulkAction.membersText}?
			</span>
		</>
	})

	const memberActionButtonText = computed(() => {
		if (GlobalStore.bulkAction.action.toLowerCase() === 'deactivate') {
			return 'Are you sure you want to deactivate?'
		}

		return GlobalStore.bulkAction.bodyText ? 'Confirm' : `${GlobalStore.bulkAction.actionText} team member`
	})

	// Actions
	const fetchMemberDetails = async (memberId: number) => {
		isLoadingMember.value = true

		try {
			const memberDetailsUrl = `/agencies/${getAgencyId()}/caregivers/${memberId}/panel`
			const response = await getMemberDetails(api, memberDetailsUrl)

			additionalTraining.value = {
				caregiver_id: memberId,
				courses: response.historical_courses,
				id: null,
			}
			agencyHasBirthdayReq.value = response.agency_has_birthday_req
			agencyStateRegistration.value = response.agency_state_registration
			backOfficeIntegration.value = {
				enabled: response.back_office_integration_enabled ?? false,
				providerName: response.back_office_integration_provider_name ?? '',
			}

			memberDetails.value = response
			memberDetails.value.caregiver = response.caregiver
			memberDetails.value.caregiver_created_at = response.caregiver_created_at
			memberDetails.value.caregiver.notes = response.caregiver.notes ? response.caregiver.notes
				.sort((a: Note, b: Note) =>
					a.created > b.created ? -1 : a.created < b.created ? 1 : 0
				) : []
			memberDetails.value.has_eligible_agencies = response.has_eligible_agencies
			memberDetails.value.historical_courses = response.historical_courses ?? []
			memberDetails.value.is_permitted_user = response.is_permitted_user
			memberDetails.value.paths = response.paths

			useTeamStore().selectedMemberId = response.caregiver.id

			if (memberDetails.value.caregiver) {
				isSuper.value = response.is_super
			}

			goals.value = response.caregiver_goals || []
			goal.value = goals.value.length === 1 ? goals.value[0] : goal.value
			eligibleAgencies.value = response.eligible_agencies

			const memberTrainingStore = useMemberTrainingStore()

			memberTrainingStore.editCourse = getDefaultCourse()
			memberTrainingStore.isCertificationsEnabled = response.is_specialized_certifications_feature_enabled
			memberTrainingStore.waiveTypes = response.waive_types

			GlobalStore.currentUser = {
				...window.current_user,
				canModifyComplianceCurriculum: response.can_modify_compliance_curriculum,
			}
		} catch (error) {
			GlobalStore.onHandleErrorMessages({ error })
		} finally {
			isLoadingMember.value = false
		}
	}

	const fetchMemberSettings = async (agencyId: string) => {
		try {
			const response = await getMemberSettings(
				api,
				agencyId
			)

			memberSettings.value = response.data
		} catch (error) {
			GlobalStore.onHandleErrorMessages({ error })
		}
	}

	const setCurrentGoal = (goalId: number | undefined) => {
		if (!goalId) {
			return
		}

		const foundGoal = goals.value?.find(g => g.id === goalId)

		if (!!foundGoal) {
			goal.value = foundGoal
		}
	}

	const updateAnnualYear = async ({
		id, path, year,
	}: { id: number; path: string; year: number }) => {
		try {
			const response = await putUpdateAnnualYear(api, { path, year })

			if ('data' in response) {
				GlobalStore.onHandleSuccessMessages({ response })
				updatedAnnualDueDate.value = { date: response.data.updated_due_date, id }
			}
		} catch (error) {
			GlobalStore.onHandleErrorMessages({ error })
		}
	}

	const updateMemberSettings = async (agencyId: string) => {
		await putEditMemberSettings(api, agencyId, { member_settings: memberSettings.value })
	}

	const updateNotes = async (notesData: NotesData) => {
		try {
			await postNotes(api,
				`/agencies/${getAgencyId()}/caregivers/add_note`,
				{
					memberId: memberDetails.value!.caregiver!.id,
					notes: notesData.notes,
				}
			)
		} catch (error) {
			GlobalStore.onHandleErrorMessages({ error })
		}
	}

	return {
		// State
		additionalTraining,
		agencyHasBirthdayReq,
		agencyStateRegistration,
		backOfficeIntegration,
		backToAdministratorDashboardUrl,
		backToAdministratorLinkType,
		currentCourseStatusId,
		eligibleAgencies,
		excludeNewTrainingBundleId,
		filteredMemberIds,
		goal,
		goals,
		groups,
		isAdvancedAutomationEnabled,
		isHireDateTheBaseDateForComplianceAnnualDueDate,
		isLoadingMember,
		isSuper,
		manageGroups,
		memberCount,
		memberCreatedAt,
		memberDetails,
		memberFilter,
		memberList,
		memberNotes,
		memberSettings,
		membersActiveCount,
		notStartedMembers,
		numberOfMembers,
		shouldDisplayInProgressMessage,
		showDueDate,
		updatedAnnualDueDate,

		// Getters
		filteredMembers,
		memberActionBody,
		memberActionButtonText,

		// Actions
		fetchMemberDetails,
		fetchMemberSettings,
		setCurrentGoal,
		updateAnnualYear,
		updateMemberSettings,
		updateNotes,
	}
})

export default useMembersStore
